import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Party, PartyRole } from '@prody/shared';
import {
  Button, CHANNEL_ICONS, Code, DataTable, EmptyState, IconPlus, Input,
  ListPane, ManifestPane, PageHeader, Segmented, Sheet, Stamp, Toolbar,
  Tooltip, vars, Workbench, type Column,
} from '~/design-system';
import { useBusiness } from '~/platform/tenancy/BusinessProvider';
import { useParties } from '~/platform/data/queries';
import { useHasManifestSpace } from '~/platform/hooks/useMediaQuery';
import { ContactManifest } from './ContactManifest';
import './contacts.css';

/* ==================================================================
   Contacts.

   One list for customers, suppliers, ambassadors, carriers and
   partner businesses, because in this trade they are the same people
   wearing different hats — the farm you buy from also buys your olive
   oil, and the consultant who introduced three restaurants is a
   customer herself. Splitting them into five screens means five
   places to update a phone number.

   Roles are a filter, not a table.
   ================================================================== */

const ROLES: (PartyRole | 'all')[] = ['all', 'customer', 'supplier', 'ambassador', 'carrier'];

export default function ContactsPage() {
  const { t } = useTranslation(['contacts', 'common']);
  const { business, format } = useBusiness();
  const [role, setRole] = useState<PartyRole | 'all'>('all');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const hasManifestSpace = useHasManifestSpace();

  const { data: parties = [], isLoading } = useParties(business.id, {
    ...(role !== 'all' ? { role } : {}),
    ...(search ? { search } : {}),
  });

  const columns = useMemo<Column<Party>[]>(
    () => [
      {
        key: 'code',
        header: t('common:labels.sku'),
        width: '120px',
        sortable: true,
        sortValue: (p) => p.code,
        cell: (p) => <Code>{p.code}</Code>,
      },
      {
        key: 'name',
        header: t('title'),
        width: 'minmax(180px, 2fr)',
        sortable: true,
        sortValue: (p) => p.name,
        cell: (p) => (
          <span className="pd-contacts__name">
            <span className="truncate">{p.name}</span>
            {p.status === 'lead' ? <Stamp tone="info" size="sm">{t('common:labels.none')}</Stamp> : null}
            {p.status === 'blocked' ? <Stamp tone="alert" size="sm">{p.status}</Stamp> : null}
          </span>
        ),
      },
      {
        key: 'roles',
        header: t('common:labels.status'),
        width: '190px',
        hideBelow: 'sm',
        cell: (p) => (
          <span className="row" style={vars({ '--gap': 'var(--s-2)' })}>
            {p.roles.map((r) => (
              <Stamp key={r} tone={r === 'supplier' ? 'info' : r === 'ambassador' ? 'accent' : 'neutral'} size="sm">
                {t(`roles.${r}`)}
              </Stamp>
            ))}
          </span>
        ),
      },
      {
        key: 'channels',
        header: t('common:actions.more'),
        width: '110px',
        hideBelow: 'md',
        /* Every reachable channel, as one strip. This is the "reach
           them everywhere" requirement rendered where it is actually
           useful — in the list, before you open anything. */
        cell: (p) => (
          <span className="pd-contacts__channels">
            {p.channels.map((c) => {
              const Icon = CHANNEL_ICONS[c.kind];
              return (
                <Tooltip key={`${c.kind}-${c.address}`} content={`${t(`common:channel.${c.kind}`)} · ${c.address}`}>
                  <span className="pd-contacts__channel" data-muted={c.optedOut || undefined}>
                    <Icon />
                  </span>
                </Tooltip>
              );
            })}
          </span>
        ),
      },
      {
        key: 'terms',
        header: t('terms'),
        width: '120px',
        hideBelow: 'lg',
        cell: (p) =>
          p.paymentTerms.netDays === 0 ? (
            <span className="t-small dim">{t('onDelivery')}</span>
          ) : (
            <span className="num">{t('netDays', { days: p.paymentTerms.netDays })}</span>
          ),
      },
      {
        key: 'stand',
        header: t('standCode'),
        width: '110px',
        hideBelow: 'lg',
        cell: (p) =>
          p.supplier?.standCode ? (
            <Code>{p.supplier.standCode}</Code>
          ) : p.supplier?.runDays.length ? (
            <span className="t-small dim">{format.list(p.supplier.runDays)}</span>
          ) : (
            <span className="faint">—</span>
          ),
      },
    ],
    [t, format],
  );

  const selected = parties.find((p) => p.id === selectedId) ?? null;

  return (
    <Workbench>
      <PageHeader
        eyebrow={t('eyebrow')}
        title={t('title')}
        count={isLoading ? undefined : t('common:labels.results', { count: parties.length })}
        actions={
          <Button variant="primary" icon={<IconPlus />}>
            {t('newContact')}
          </Button>
        }
      />

      <Toolbar
        trailing={
          <Input
            type="search"
            placeholder={t('common:actions.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ inlineSize: 220 }}
          />
        }
      >
        <Segmented<PartyRole | 'all'>
          label={t('common:labels.status')}
          value={role}
          onChange={setRole}
          options={ROLES.map((r) => ({
            value: r,
            label: r === 'all' ? t('common:labels.all') : t(`roles.${r}`),
          }))}
        />
      </Toolbar>

      <ListPane>
        <DataTable<Party>
          rows={parties}
          columns={columns}
          rowKey={(p) => p.id}
          rowMeta={(p) => ({
            handlingClass: p.roles.includes('supplier') ? 'chilled' : 'neutral',
            struck: p.status === 'blocked',
          })}
          onRowActivate={(p) => setSelectedId(p.id)}
          activeRowKey={selectedId}
          loading={isLoading}
          caption={t('title')}
          empty={
            <EmptyState
              title={search ? t('common:empty.noResults') : t('empty')}
              body={search ? t('common:empty.noResultsBody') : t('emptyBody')}
              action={
                !search ? (
                  <Button variant="primary" icon={<IconPlus />}>
                    {t('newContact')}
                  </Button>
                ) : null
              }
            />
          }
        />
      </ListPane>

      {/* Keyed so switching contacts re-runs the manifest cascade. */}
      {hasManifestSpace ? (
        <ManifestPane open={Boolean(selected)}>
          {selected ? (
            <ContactManifest key={selected.id} party={selected} onClose={() => setSelectedId(null)} />
          ) : null}
        </ManifestPane>
      ) : (
        <Sheet
          open={Boolean(selected)}
          onOpenChange={(open) => !open && setSelectedId(null)}
          title={selected?.name ?? ''}
        >
          {selected ? (
            <ContactManifest key={selected.id} party={selected} onClose={() => setSelectedId(null)} embedded />
          ) : null}
        </Sheet>
      )}
    </Workbench>
  );
}
